import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PickerSheet } from "@/components/ui/PickerSheet";
import {
	ALL_ELEMENTS,
	ALL_WEAPONS,
	getEquipmentByAccess,
	getEquipmentById,
	getWeaponById,
	getWeaponsForFaction,
} from "@/data";
import { useIsMobile } from "@/hooks/useBreakpoint";
import { useForceStore } from "@/store/forceStore";
import type { ForceElement, ForceModel } from "@/types/force";
import type { EquipmentCategory, WeaponCategory } from "@/types/game-data";
import { calculateModelTotalCost } from "@/utils/points";

interface ModelEditorProps {
	element: ForceElement;
	model: ForceModel;
}

const ROLE_LABELS: Record<string, string> = {
	commander: "Commander",
	sergeant: "Sergeant",
	soldier: "Soldier",
};

const ROLE_BADGE_VARIANTS: Record<string, "amber" | "blue" | "gray"> = {
	commander: "amber",
	sergeant: "blue",
	soldier: "gray",
};

/** Display order and labels for weapon categories in the picker */
const WEAPON_CATEGORY_ORDER: readonly WeaponCategory[] = [
	"rifle",
	"smg",
	"shotgun",
	"sidearm",
	"marksman",
	"machinegun",
	"launcher",
	"closecombat",
	"thrown",
	"specialist",
	"emplaced",
];

const WEAPON_CATEGORY_LABELS: Record<WeaponCategory, string> = {
	rifle: "Rifles",
	smg: "SMGs",
	shotgun: "Shotguns",
	sidearm: "Sidearms",
	marksman: "Marksman Rifles",
	machinegun: "Machine Guns",
	launcher: "Launchers",
	closecombat: "Close Combat",
	thrown: "Thrown",
	specialist: "Specialist",
	emplaced: "Emplaced",
};

/** Display order and labels for equipment categories in the picker */
const EQUIPMENT_CATEGORY_ORDER: readonly EquipmentCategory[] = [
	"armour",
	"build",
	"optics",
	"comms",
	"protection",
	"breaching",
	"k9",
	"ordnance",
];

const EQUIPMENT_CATEGORY_LABELS: Record<EquipmentCategory, string> = {
	armour: "Armour",
	build: "Weapon Build",
	optics: "Optics & Vision",
	comms: "Comms & Observation",
	protection: "Protection",
	breaching: "Breaching",
	k9: "K9",
	ordnance: "Ordnance & Misc",
};

type PickerMode = "weapon" | "equipment" | null;

export function ModelEditor({ element, model }: ModelEditorProps) {
	const removeModel = useForceStore((s) => s.removeModel);
	const duplicateModel = useForceStore((s) => s.duplicateModel);
	const addWeapon = useForceStore((s) => s.addWeapon);
	const removeWeapon = useForceStore((s) => s.removeWeapon);
	const addEquipment = useForceStore((s) => s.addEquipment);
	const removeEquipment = useForceStore((s) => s.removeEquipment);
	const setModelRole = useForceStore((s) => s.setModelRole);
	const faction = useForceStore((s) => {
		const active = s.rosters.find((r) => r.id === s.activeRosterId);
		return active?.faction ?? "taskforce";
	});

	const isMobile = useIsMobile();
	const [picker, setPicker] = useState<PickerMode>(null);

	const template = ALL_ELEMENTS.find((t) => t.id === element.templateId);
	const totalCost = calculateModelTotalCost(model, element);
	const maxModels = template?.maxModels ?? 999;
	const slotsRemaining = Math.max(0, maxModels - element.models.length);
	const canDuplicate = slotsRemaining > 0 && (model.weapons.length > 0 || model.equipment.length > 0);

	// Get available weapons filtered by faction (deduplicated by name)
	const factionWeapons = useMemo(() => getWeaponsForFaction(ALL_WEAPONS, faction), [faction]);

	// All weapons this model can access: primary slots + specialist slots allowed by element
	const availableWeapons = useMemo(() => {
		return factionWeapons.filter((w) => {
			if (w.slot === "specialist") {
				return template?.specialistWeaponCategories.includes(w.category as WeaponCategory);
			}
			return true; // primary, sidearm, thrown, closecombat are always available
		});
	}, [factionWeapons, template]);

	// Group weapons by category in display order
	const groupedWeapons = useMemo(() => {
		const groups = new Map<WeaponCategory, typeof availableWeapons>();
		for (const w of availableWeapons) {
			const cat = w.category as WeaponCategory;
			const list = groups.get(cat) ?? [];
			list.push(w);
			groups.set(cat, list);
		}
		return WEAPON_CATEGORY_ORDER
			.filter((cat) => groups.has(cat))
			.map((cat) => ({ category: cat, label: WEAPON_CATEGORY_LABELS[cat], items: groups.get(cat)! }));
	}, [availableWeapons]);

	// Get available equipment
	const availableEquipment = template
		? getEquipmentByAccess(template.equipmentAccess as unknown as readonly string[])
		: [];
	// Filter tier1and2 equipment based on actual tier
	const filteredEquipment = availableEquipment.filter((e) => {
		if (e.access === "tier1and2") {
			return element.tier === "tier1" || element.tier === "tier2";
		}
		return true;
	});

	// Group filtered equipment by category in display order
	const groupedEquipment = useMemo(() => {
		const groups = new Map<EquipmentCategory, typeof filteredEquipment>();
		for (const e of filteredEquipment) {
			const list = groups.get(e.category) ?? [];
			list.push(e);
			groups.set(e.category, list);
		}
		// Return only categories that have items, in the defined order
		return EQUIPMENT_CATEGORY_ORDER
			.filter((cat) => groups.has(cat))
			.map((cat) => ({ category: cat, label: EQUIPMENT_CATEGORY_LABELS[cat], items: groups.get(cat)! }));
	}, [filteredEquipment]);

	/** Shared weapon picker content — grouped by category */
	function renderWeaponPickerContent() {
		return (
			<div className="space-y-3">
				{groupedWeapons.map((group) => (
					<div key={group.category}>
						<div className="text-xs text-text-muted font-semibold uppercase mb-1.5">
							{group.label}
						</div>
						<div className="flex flex-wrap gap-1.5">
							{group.items.map((w) => (
								<button
									key={w.id}
									type="button"
									onClick={() => {
										addWeapon(element.id, model.id, w.id);
										setPicker(null);
									}}
									className={`text-xs px-3 py-2 rounded bg-bg-tertiary hover:bg-bg-elevated transition-colors cursor-pointer min-h-[44px] lg:min-h-0 lg:py-1 lg:px-2 ${
										w.slot === "specialist"
											? "text-accent-secondary"
											: "text-text-secondary hover:text-text-primary"
									}`}
								>
									{w.name} ({w.cost}r)
								</button>
							))}
						</div>
					</div>
				))}
			</div>
		);
	}

	/** Shared equipment picker content — grouped by category */
	function renderEquipmentPickerContent() {
		return (
			<div className="space-y-3">
				{groupedEquipment.map((group) => (
					<div key={group.category}>
						<div className="text-xs text-text-muted font-semibold uppercase mb-1.5">
							{group.label}
						</div>
						<div className="flex flex-wrap gap-1.5">
							{group.items.map((e) => (
								<button
									key={e.id}
									type="button"
									onClick={() => {
										addEquipment(element.id, model.id, e.id);
										setPicker(null);
									}}
									className="text-xs px-3 py-2 rounded bg-bg-tertiary text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors cursor-pointer min-h-[44px] lg:min-h-0 lg:py-1 lg:px-2"
								>
									{e.name} ({e.cost}r)
								</button>
							))}
						</div>
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="border border-border-default rounded-md p-3 bg-bg-primary/50 space-y-2">
			{/* Model header */}
			<div className="flex items-center justify-between gap-1">
				<div className="flex items-center gap-2 min-w-0">
					<Badge variant={ROLE_BADGE_VARIANTS[model.role] ?? "gray"} size="sm">
						{ROLE_LABELS[model.role] ?? model.role}
					</Badge>
					{model.customName && (
						<span className="text-sm text-text-primary truncate">{model.customName}</span>
					)}
				</div>
				<div className="flex items-center gap-1 lg:gap-2 shrink-0">
					<span className="font-mono text-sm text-accent-secondary">{totalCost}r</span>
					{model.role === "soldier" && template?.allowSergeant && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setModelRole(element.id, model.id, "sergeant")}
							title="Promote to Sergeant (+10r)"
						>
							Sgt
						</Button>
					)}
					{model.role === "sergeant" && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setModelRole(element.id, model.id, "soldier")}
							title="Demote to Soldier"
						>
							Demote
						</Button>
					)}
					{canDuplicate && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => duplicateModel(element.id, model.id, 1)}
							title={`Duplicate this soldier with same loadout (${slotsRemaining} slot${slotsRemaining !== 1 ? "s" : ""} remaining)`}
						>
							+Copy
						</Button>
					)}
					{model.role !== "commander" && (
						<button
							type="button"
							onClick={() => removeModel(element.id, model.id)}
							className="min-w-[44px] min-h-[44px] lg:min-w-0 lg:min-h-0 flex items-center justify-center text-text-muted hover:text-accent-danger transition-colors cursor-pointer"
						>
							&times;
						</button>
					)}
				</div>
			</div>

			{/* Current loadout */}
			<div className="flex flex-wrap gap-1">
				{model.weapons.map((wId, _idx) => {
					const weapon = getWeaponById(wId);
					return weapon ? (
						<Badge key={`w-${wId}-${model.id}-${_idx}`} variant="default" size="sm">
							{weapon.name} ({weapon.cost}r)
							<button
								type="button"
								className="ml-1 text-text-muted hover:text-accent-danger cursor-pointer p-1 -mr-1"
								onClick={() => removeWeapon(element.id, model.id, wId)}
							>
								&times;
							</button>
						</Badge>
					) : null;
				})}
				{model.equipment.map((eId, _idx) => {
					const equip = getEquipmentById(eId);
					return equip ? (
						<Badge key={`e-${eId}-${model.id}-${_idx}`} variant="blue" size="sm">
							{equip.name} ({equip.cost}r)
							<button
								type="button"
								className="ml-1 text-text-muted hover:text-accent-danger cursor-pointer p-1 -mr-1"
								onClick={() => removeEquipment(element.id, model.id, eId)}
							>
								&times;
							</button>
						</Badge>
					) : null;
				})}
			</div>

			{/* Add weapon / equipment buttons */}
			<div className="flex gap-2">
				<Button
					variant="ghost"
					size="sm"
					onClick={() => setPicker(picker === "weapon" ? null : "weapon")}
				>
					+ Weapon
				</Button>
				<Button
					variant="ghost"
					size="sm"
					onClick={() => setPicker(picker === "equipment" ? null : "equipment")}
				>
					+ Equipment
				</Button>
			</div>

			{/* --- Pickers: mobile = PickerSheet, desktop = inline dropdown --- */}

			{isMobile ? (
				<>
					<PickerSheet
						isOpen={picker === "weapon"}
						onClose={() => setPicker(null)}
						title="Select Weapon"
					>
						{renderWeaponPickerContent()}
					</PickerSheet>

					<PickerSheet
						isOpen={picker === "equipment"}
						onClose={() => setPicker(null)}
						title="Select Equipment"
					>
						{renderEquipmentPickerContent()}
					</PickerSheet>
				</>
			) : (
				<>
					{/* Desktop: Weapon picker dropdown */}
					{picker === "weapon" && (
						<div className="border border-border-default rounded-md bg-bg-secondary p-2 max-h-60 overflow-y-auto">
							{renderWeaponPickerContent()}
						</div>
					)}

					{/* Desktop: Equipment picker dropdown */}
					{picker === "equipment" && (
						<div className="border border-border-default rounded-md bg-bg-secondary p-2 max-h-60 overflow-y-auto">
							{renderEquipmentPickerContent()}
						</div>
					)}
				</>
			)}
		</div>
	);
}
